import { Component, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../core/api-client.service';
import { AuthService } from '../core/auth.service';
import { I18nService } from '../core/i18n.service';
import { RealtimeService } from '../core/realtime.service';
import { SeoService } from '../core/seo.service';
import { AuthWidgetComponent } from '../widgets/auth/auth-widget.component';
import { formatLocalDate } from '../core/local-date.util';

interface NotificationItem {
  id: number;
  eventKey: string;
  title: string;
  body: string | null;
  url: string | null;
  icon: string | null;
  readAt: string | null;
  createdAt: string;
}

interface NotificationResponse {
  success?: boolean;
  data?: {
    result?: boolean;
    items?: NotificationItem[];
    unreadCount?: number;
    hasMore?: boolean;
  };
}

@Component({
  selector: 'app-notifications-page',
  imports: [AuthWidgetComponent, RouterLink],
  template: `
    <section class="notifications-page">
      <header class="page-title">
        <p class="eyebrow">{{ siteName }}</p>
        <h1>{{ i18n.t('notifications') }}</h1>
        <p>{{ copy().lead }}</p>
      </header>

      @if (!auth.loggedIn()) {
        <div class="login-card">
          <h2>{{ copy().login }}</h2>
          <app-auth-widget />
        </div>
      } @else {
        <div class="toolbar">
          <strong>{{ unreadCount() }} {{ copy().unread }}</strong>
          <button type="button" (click)="markAllRead()">{{ copy().markAll }}</button>
        </div>

        <div class="notification-list">
          @for (item of items(); track item.id) {
            <button type="button" class="notification-card" [class.unread]="!item.readAt" (click)="open(item)">
              <span class="icon">{{ item.icon || 'bell' }}</span>
              <div>
                <strong>{{ item.title }}</strong>
                @if (item.body) {
                  <p>{{ item.body }}</p>
                }
                <small>{{ readableDate(item.createdAt) }}</small>
              </div>
              <i>{{ item.readAt ? copy().read : copy().new }}</i>
            </button>
          } @empty {
            <div class="empty">
              <h2>{{ copy().empty }}</h2>
              <p>{{ copy().emptyLead }}</p>
              <a [routerLink]="i18n.routePath('fortune')">{{ copy().explore }}</a>
            </div>
          }
        </div>
      }
    </section>
  `,
  styles: [`
    .notifications-page { display: grid; gap: 18px; margin: 0 auto; max-width: 920px; }
    .page-title, .login-card, .toolbar, .notification-card, .empty {
      background: color-mix(in srgb, var(--surface) 80%, transparent);
      border: 1px solid var(--border);
      border-radius: 26px;
      box-shadow: 0 18px 52px color-mix(in srgb, var(--shadow) 28%, transparent);
    }
    .page-title, .login-card, .empty { padding: clamp(20px, 3vw, 30px); }
    .page-title h1 { font-size: clamp(30px, 4vw, 48px); margin: 0 0 8px; }
    .page-title p:last-child, .empty p { color: var(--muted); line-height: 1.55; }
    .toolbar {
      align-items: center;
      display: flex;
      justify-content: space-between;
      padding: 14px 18px;
    }
    .toolbar button, .empty a {
      background: var(--ink);
      border: 0;
      border-radius: 999px;
      color: var(--paper);
      cursor: pointer;
      font: inherit;
      font-weight: 950;
      padding: 10px 14px;
      text-decoration: none;
    }
    .notification-list { display: grid; gap: 12px; }
    .notification-card {
      align-items: center;
      color: var(--ink);
      cursor: pointer;
      display: grid;
      gap: 14px;
      grid-template-columns: auto 1fr auto;
      padding: 14px;
      text-align: left;
      width: 100%;
    }
    .notification-card.unread { border-color: color-mix(in srgb, var(--accent) 48%, var(--border)); }
    .notification-card .icon {
      align-items: center;
      background: color-mix(in srgb, var(--accent) 16%, transparent);
      border-radius: 18px;
      display: inline-flex;
      font-size: 12px;
      font-weight: 950;
      height: 54px;
      justify-content: center;
      width: 54px;
    }
    .notification-card strong { display: block; font-weight: 950; }
    .notification-card p { color: var(--muted); margin: 4px 0; }
    .notification-card small, .notification-card i { color: var(--muted); font-style: normal; font-weight: 800; }
    @media (max-width: 640px) {
      .toolbar, .notification-card { align-items: stretch; grid-template-columns: 1fr; }
      .toolbar { gap: 12px; }
    }
  `]
})
export class NotificationsPage {
  protected readonly auth = inject(AuthService);
  protected readonly i18n = inject(I18nService);
  protected readonly siteName = 'Fal Deryası';
  protected readonly copy = computed(() => ({
    tr: { lead: 'Giriş, sipariş, destek ve hesap hareketlerini tek yerden takip et.', login: 'Bildirimlerini görmek için giriş yap', unread: 'okunmamış bildirim', markAll: 'Tümünü okundu yap', read: 'Okundu', new: 'Yeni', empty: 'Henüz bildirimin yok', emptyLead: 'Yeni siparişler, destek yanıtları ve hesap hareketleri burada görünecek.', explore: 'Fal baktırmaya başla' },
    en: { lead: 'Track account, order and support updates in one place.', login: 'Sign in to view your notifications', unread: 'unread notifications', markAll: 'Mark all as read', read: 'Read', new: 'New', empty: 'No notifications yet', emptyLead: 'Orders, support replies and account updates will appear here.', explore: 'Explore readings' },
    de: { lead: 'Verfolge Konto-, Bestell- und Supportmeldungen an einem Ort.', login: 'Melde dich an, um Benachrichtigungen zu sehen', unread: 'ungelesene Benachrichtigungen', markAll: 'Alle als gelesen markieren', read: 'Gelesen', new: 'Neu', empty: 'Noch keine Benachrichtigungen', emptyLead: 'Bestellungen, Supportantworten und Kontomeldungen erscheinen hier.', explore: 'Deutungen entdecken' },
    es: { lead: 'Consulta las novedades de cuenta, pedidos y soporte.', login: 'Inicia sesión para ver tus notificaciones', unread: 'notificaciones sin leer', markAll: 'Marcar todo como leído', read: 'Leído', new: 'Nuevo', empty: 'Aún no hay notificaciones', emptyLead: 'Los pedidos, respuestas de soporte y novedades aparecerán aquí.', explore: 'Explorar lecturas' },
    fr: { lead: 'Suivez les mises à jour du compte, des commandes et de l’assistance.', login: 'Connectez-vous pour voir vos notifications', unread: 'notifications non lues', markAll: 'Tout marquer comme lu', read: 'Lu', new: 'Nouveau', empty: 'Aucune notification', emptyLead: 'Les commandes, réponses du support et mises à jour apparaîtront ici.', explore: 'Découvrir les voyances' }
  }[this.i18n.lang()]));
  private readonly api = inject(ApiClientService);
  private readonly router = inject(Router);
  private readonly realtime = inject(RealtimeService);
  private readonly seo = inject(SeoService);
  protected readonly items = this.realtime.notifications;
  protected readonly unreadCount = this.realtime.unreadCount;

  constructor() {
    this.seo.setPage({
      title: this.i18n.t('notifications'),
      description: this.i18n.t('notificationsSeoDescription'),
      path: this.i18n.routePath('notifications')
    });
    void this.auth.refresh().then(() => this.realtime.loadNotifications(40));
  }

  protected async open(item: NotificationItem): Promise<void> {
    await this.markRead(item.id);
    if (item.url) {
      void this.router.navigateByUrl(item.url);
    }
  }

  protected async markAllRead(): Promise<void> {
    await this.markRead(0);
  }

  protected readableDate(value: string): string {
    return formatLocalDate(value, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }, this.i18n.lang());
  }

  private async load(): Promise<void> {
    if (!this.auth.loggedIn()) {
      return;
    }
    await this.realtime.loadNotifications(40);
  }

  private async markRead(notificationID: number): Promise<void> {
    await firstValueFrom(this.api.post<unknown>('notificationRead', notificationID > 0 ? { notificationID } : {}));
    await this.load();
  }
}
